import type { ApiEnvelope } from './types';

export class ApiError extends Error {
  status: number;

  /**
   * 功能：创建带 HTTP 状态码的前端接口异常。
   * 参数：message（接口错误说明）；status（HTTP 状态码）。
   */
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/**
 * 功能：读取当前会话中的管理员密钥。
 * 参数：无。
 */
export function getAdminKey(): string {
  return sessionStorage.getItem('smartmail_admin_key') || '';
}

/**
 * 功能：保存或清除当前会话管理员密钥。
 * 参数：key（管理员密钥，为空时清除）。
 */
export function setAdminKey(key: string): void {
  if (key) sessionStorage.setItem('smartmail_admin_key', key);
  else sessionStorage.removeItem('smartmail_admin_key');
}

/**
 * 功能：向 smartMail API 发起 JSON 请求并统一处理鉴权和错误。
 * 参数：path（API 相对路径）；options（标准 fetch RequestInit 参数）。
 */
export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body) headers.set('Content-Type', 'application/json');
  const key = getAdminKey();
  if (key) headers.set('Authorization', `Bearer ${key}`);
  const response = await fetch(path, { ...options, headers });
  const payload = await response.json().catch(() => ({ ok: false, message: `HTTP ${response.status}` })) as ApiEnvelope<T>;
  if (!response.ok || payload.ok === false) throw new ApiError(payload.message || `HTTP ${response.status}`, response.status);
  return payload.data;
}

/**
 * 功能：使用 XMLHttpRequest 直接上传文件并报告浏览器端发送进度。
 * 参数：path（流式导入地址）；file（待上传文件）；onProgress（0 到 100 的进度回调）。
 */
export function uploadFile<T>(path: string, file: File, onProgress: (progress: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', path);
    request.setRequestHeader('Content-Type', 'application/octet-stream');
    const key = getAdminKey();
    if (key) request.setRequestHeader('Authorization', `Bearer ${key}`);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    request.onerror = () => reject(new ApiError('文件上传连接失败', 0));
    request.onload = () => {
      let payload: ApiEnvelope<T>;
      try { payload = JSON.parse(request.responseText) as ApiEnvelope<T>; }
      catch { reject(new ApiError(`HTTP ${request.status}`, request.status)); return; }
      if (request.status < 200 || request.status >= 300 || payload.ok === false) {
        reject(new ApiError(payload.message || `HTTP ${request.status}`, request.status));
        return;
      }
      onProgress(100);
      resolve(payload.data);
    };
    request.send(file);
  });
}

/**
 * 功能：下载需要鉴权的导出文件，并在浏览器触发保存。
 * 参数：url（下载地址）；fallbackName（响应未提供文件名时使用的名称）。
 */
export async function downloadFile(url: string, fallbackName: string): Promise<void> {
  const headers = new Headers();
  const key = getAdminKey();
  if (key) headers.set('Authorization', `Bearer ${key}`);
  const response = await fetch(url, { headers });
  if (!response.ok) throw new ApiError(`导出失败：HTTP ${response.status}`, response.status);
  const disposition = response.headers.get('content-disposition') || '';
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || fallbackName;
  const picker = (window as Window & {
    showSaveFilePicker?: (options: { suggestedName: string }) => Promise<{ createWritable: () => Promise<WritableStream> }>;
  }).showSaveFilePicker;
  if (picker && response.body) {
    const handle = await picker({ suggestedName: filename });
    const writable = await handle.createWritable();
    await response.body.pipeTo(writable);
    return;
  }
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(href);
}
